import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ConfigurationStorageService } from '../../configuration-storage.service';

import { CameraQrScanService } from './camera-qr-scan.service';
import { IQrCodeScanResult, IQrCodeScanningConfig } from './interfaces';

export type CameraQrScanConfig = Omit<Partial<IQrCodeScanningConfig>, 'videoElementSelector' | 'videoElement'>
    & Partial<Pick<IQrCodeScanningConfig, 'ignoreSameCodesForMs'>>;

@Component({
    selector: 'aoc-camera-qr-scan',
    templateUrl: 'camera-qr-scan.component.html',
    styleUrls: ['camera-qr-scan.component.css'],
    standalone: false
})
export class CameraQrScanComponent implements AfterViewInit, OnDestroy {
    @Input() startScan: Observable<void>;
    @Input() stopScan: Observable<void>;
    @Input() qrCodeScanned: Subject<IQrCodeScanResult>;
    @Input() qrCodeScanError: Subject<string | Error>;
    @Input() config?: CameraQrScanConfig;
    @Input() startScanOnInit = false;

    videoElementRef$ = new BehaviorSubject<ElementRef<HTMLVideoElement> | null>(null);

    @ViewChild('videoElement', { static: true })
    public set videoElement(value: ElementRef) {
        this.videoElementRef$.next(value);
    }

    private readonly destroyed$ = new Subject<void>();

    constructor(private readonly cameraQrScanSvc: CameraQrScanService, private configService: ConfigurationStorageService) { }

    async ngAfterViewInit(): Promise<void> {
        if (this.startScan) {
            this.startScan.pipe(
                takeUntil(this.destroyed$)
            ).subscribe(() => this.startScanRequested());
        }
        if (this.stopScan) {
            this.stopScan.pipe(
                takeUntil(this.destroyed$)
            ).subscribe(() => this.stopScanRequested());
        }
        if (this.qrCodeScanned) {
            this.cameraQrScanSvc.getScanResultObservable().pipe(
                takeUntil(this.destroyed$)
            ).subscribe(scanResult => this.qrCodeScanned.next(scanResult));
        }
        if (this.qrCodeScanError) {
            this.cameraQrScanSvc.getScanErrorObservable().pipe(
                takeUntil(this.destroyed$)
            ).subscribe(err => this.qrCodeScanError.next(err));
        }

        if (this.startScanOnInit) {
            await this.startQrScanning();
        }
    }

    async startQrScanning(): Promise<void> {
        this.setCameraQrScanInversionMode();
        const config = await this.getCameraScanConfig();
        await this.cameraQrScanSvc.start(config);
        if (config.preferredCamera){
            this.cameraQrScanSvc.setPreferredCamera(config.preferredCamera);
        }
    }

    async setCameraQrScanInversionMode(): Promise<void> {
        const qrCodeScanningSettings = this.configService.getQrCodeScanningSettings();
        await this.cameraQrScanSvc.setInversionMode(qrCodeScanningSettings.cameraInversionMode);
    }

    ngOnDestroy(): void {
        this.cameraQrScanSvc.stop();
        this.cameraQrScanSvc.destroy();
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private async getCameraScanConfig(): Promise<IQrCodeScanningConfig> {
        return {
            ...this.config,
            ignoreSameCodesForMs: this.config.ignoreSameCodesForMs
                ?? this.configService.getHandheldScannerSettings().ignoreSameCodesForMs,
            videoElement: (await this.getVideoElement()).nativeElement
        };
    }

    private async startScanRequested(): Promise<void> {
        await this.startQrScanning();
    }

    private async stopScanRequested(): Promise<void> {
        return this.cameraQrScanSvc.stop();
    }

    private async getVideoElement() {
        return firstValueFrom(this.videoElementRef$.pipe(
            filter(element => !!element)
        ));
    }
}
